import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, filter, first, map, Observable, of, switchMap, take } from 'rxjs';
import { departmentsActions } from '../shared/store/departments/departments.actions';
import { selectIsDepartmentStateLoaded } from '../shared/store/departments/departments.selectors';
import { lifeCycleActions } from '../shared/store/life-cycle/life-cycle.actions';
import { selectIsStoreInitialized } from '../shared/store/life-cycle/life-cycle.selectors';

@Injectable({ providedIn: 'root' })
export class StoreInitializationService {
  private readonly store = inject(Store);

  ensureInitialized(clientCode?: string): Observable<boolean> {
    if (clientCode) {
      this.store.dispatch(departmentsActions.ensureClientDepartments({ clientCode }));
    }

    return this.store.select(selectIsStoreInitialized).pipe(
      first(),
      switchMap((isStoreInitialized) => {
        if (!isStoreInitialized) {
          this.store.dispatch(lifeCycleActions.initialize());
        }
        return combineLatest([
          this.store.select(selectIsStoreInitialized),
          this.areClientDepartmentsLoaded$(clientCode),
        ]).pipe(
          map(([isReady, areDepartmentsLoaded]) => isReady && areDepartmentsLoaded),
          filter((isReady) => isReady),
          take(1),
        );
      }),
    );
  }

  get isInitialized$(): Observable<boolean> {
    return this.store.select(selectIsStoreInitialized);
  }

  private areClientDepartmentsLoaded$(clientCode?: string): Observable<boolean> {
    if (!clientCode) {
      return of(true);
    }
    return this.store.select(selectIsDepartmentStateLoaded(clientCode));
  }
}
